import router, { asyncRoutes, constantRoutes, resetRouter } from '@/router'
import { getCurrentAccountSet } from '@/api/accountSet';

/**
 * Use meta.role to determine if the current user has permission
 * @param roles
 * @param route
 */
function hasPermission (roles, route) {
  if (route.meta && route.meta.roles) {
    return roles.some(role => route.meta.roles.includes(role))
  } else {
    return true
  }
}

/**
 * Filter asynchronous routing tables by recursion
 * @param routes asyncRoutes
 * @param roles
 */
export function filterAsyncRoutes (routes, roles) {
  const res = []

  routes.forEach(route => {
    const tmp = { ...route }
    if (hasPermission(roles, tmp)) {
      if (tmp.children) {
        tmp.children = filterAsyncRoutes(tmp.children, roles)
      }
      res.push(tmp)
    }
  })

  return res
}

const versionEnums = {
  Stable: 0,
  Beta: 1,
  Alpha: 2
}

function normalizeVersion (version) {
  if (!version || typeof version !== 'string') {
    return 'Stable';
  }
  return version.charAt(0).toUpperCase() + version.slice(1).toLowerCase();
}

function getSystemVersionLevel (version) {
  const normalizedVersion = normalizeVersion(version);
  return versionEnums[normalizedVersion] || versionEnums.Stable;
}

function normalizeCode (code) {
  if (code === undefined || code === null || code === '') {
    return null;
  }
  return String(code);
}

function hasMenuAccess (route, menuMap) {
  const menuCode = normalizeCode(route && route.meta && route.meta.menuCode);
  if (!menuCode) {
    return true;
  }
  return !!menuMap[menuCode];
}

function normalizeAccountSetType (accountSetType) {
  if (accountSetType === undefined || accountSetType === null || accountSetType === '') {
    return null;
  }

  const normalizedAccountSetType = Number(accountSetType);
  if (Number.isNaN(normalizedAccountSetType)) {
    return null;
  }

  return normalizedAccountSetType;
}

function getAccountSetTypeFromState (rootState, accountSet) {
  const user = rootState && rootState.user && rootState.user.user ? rootState.user.user : {};
  if (user.accountSetType !== undefined && user.accountSetType !== null && user.accountSetType !== '') {
    return user.accountSetType;
  }

  return accountSet && accountSet.accountSetType;
}

function hasAccountSetTypeAccess (route, accountSetType) {
  const accountSetTypes = route && route.meta && route.meta.accountSetTypes;
  if (!Array.isArray(accountSetTypes) || accountSetTypes.length === 0) {
    return true;
  }

  return accountSetTypes.map(item => Number(item)).includes(accountSetType);
}

function filterRoutesByVersion (routes, sysVersion, showDividend) {
  const res = []
  const normalizedSysVersion = normalizeVersion(sysVersion);
  const currentVersionLevel = getSystemVersionLevel(normalizedSysVersion);

  for (const route of routes) {
    const item = { ...route }
    if (item.meta) {
      const menuVersion = item.meta.version || 'Stable';
      const menuVersionLevel = versionEnums[menuVersion] || versionEnums.Stable;
      if (currentVersionLevel < menuVersionLevel) {
        continue;
      }
      if (item.meta.isDividend) {
        if (normalizedSysVersion !== 'Alpha' || !showDividend) {
          continue;
        }
      }
    }
    if (item.children) {
      item.children = filterRoutesByVersion(item.children, normalizedSysVersion, showDividend);
    }
    res.push(item)
  }

  return res;
}

function filterRoutesByAccountSetType (routes, accountSetType) {
  const normalizedAccountSetType = normalizeAccountSetType(accountSetType);
  if (normalizedAccountSetType === null) {
    return routes;
  }

  const res = [];

  for (const route of routes) {
    const item = { ...route };

    if (!hasAccountSetTypeAccess(item, normalizedAccountSetType)) {
      continue;
    }

    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    if (hasChildren) {
      item.children = filterRoutesByAccountSetType(item.children, normalizedAccountSetType);
    }

    if (hasChildren && item.children.length === 0 && !item.hidden) {
      continue;
    }

    res.push(item);
  }

  return res;
}

function filterRoutesByMenuCodes (routes, isCashier, menuMap = {}) {
  if (!isCashier) {
    return routes;
  }

  const res = [];

  for (const route of routes) {
    const item = { ...route };

    if (!hasMenuAccess(item, menuMap)) {
      continue;
    }

    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    if (hasChildren) {
      item.children = filterRoutesByMenuCodes(item.children, isCashier, menuMap);
    }

    if (hasChildren && item.children.length === 0 && !item.hidden) {
      continue;
    }

    res.push(item);
  }

  return res;
}

const state = {
  routes: [],
  addRoutes: [],
  routesAdded: false
}

const mutations = {
  SET_ROUTES: (state, routes) => {
    state.addRoutes = routes
    state.routes = constantRoutes.concat(routes)
    state.routesAdded = true
  },
  RESET_ROUTES: (state) => {
    state.addRoutes = []
    state.routes = []
    state.routesAdded = false
  }
}

const actions = {
  async generateRoutes ({ commit }) {
    const accessedRoutes = asyncRoutes || []
    commit('SET_ROUTES', accessedRoutes)
    return accessedRoutes
  },
  async filterRouters ({ commit, rootState }) {
    const accountSet = getCurrentAccountSet() || {}
    const sysVersion = accountSet.sysVersion || 'Stable';
    const showDividend = !!accountSet.showDividend;
    const isCashier = !!rootState.user.isCashier;
    const menuMap = rootState.user.menus || {};
    const accessedRoutes = asyncRoutes || []
    const versionRoutes = filterRoutesByVersion(accessedRoutes, sysVersion, showDividend)
    const accountSetType = getAccountSetTypeFromState(rootState, accountSet);
    const accountSetTypeRoutes = filterRoutesByAccountSetType(versionRoutes, accountSetType)
    const menuFilteredRoutes = filterRoutesByMenuCodes(accountSetTypeRoutes, isCashier, menuMap)
    commit('SET_ROUTES', menuFilteredRoutes)
    return menuFilteredRoutes
  },
  resetRouters ({ commit }) {
    commit('RESET_ROUTES')
  },
  async refreshRouters ({ dispatch }) {
    resetRouter();
    const accessRoutes = await dispatch('filterRouters');
    router.addRoutes(accessRoutes);
    return accessRoutes;
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
